import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { createUserAPI, getUserAPI, updateUserAPI } from 'api/user';
import { getAuth, signOut } from 'firebase/auth';
import { RootState } from 'store';

interface UserState {
  data: User<UserRole> | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  /**
   * The Firebase account exists but the backend has no profile for it (GET /users/{uid} → 404).
   * The user must complete the sign-up on the "complete profile" page.
   */
  profileMissing: boolean;
  updateUser: 'init' | 'loading' | 'succeeded' | 'failed';
}

const initialState: UserState = {
  data: null,
  status: 'idle',
  error: null,
  profileMissing: false,
  updateUser: 'init',
};

/** Marker returned by fetchUserDetails when the backend has no profile for this account. */
export const PROFILE_NOT_FOUND = 404;

export const fetchUserDetails = createAsyncThunk(
  'user/fetchUserDetails',
  async (uid: string, { rejectWithValue, dispatch }) => {
    try {
      const response = await getUserAPI(uid);
      if (typeof response !== 'number') {
        return response;
      }
      if (response === 401) {
        // Token rejected by the backend: the session is unusable, start over.
        await signOut(getAuth());
        dispatch(resetUserState());
        return rejectWithValue(401);
      }
      if (response === PROFILE_NOT_FOUND) {
        return PROFILE_NOT_FOUND;
      }
      return rejectWithValue(response);
    } catch (error) {
      return rejectWithValue(0); // network error
    }
  }
);

export const updateUserDetails = createAsyncThunk(
  'user/updateUserDetails',
  async (data: Partial<User<UserRole>>, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await updateUserAPI<UserRole>(data);
      if (response.status === 204) {
        const { user } = (getState() as RootState).auth;
        dispatch(fetchUserDetails(user?.uid || ''));
      }
      return;
    } catch (error) {
      return rejectWithValue('');
    }
  }
);

export const createNewUser = createAsyncThunk(
  'user/createUser',
  async (
    data: CreateUser<UserPersonalData | CreateUserRecruiterPartData>,
    { rejectWithValue, dispatch }) => {
    let posted = false;
    try {
      posted = await createUserAPI<UserPersonalData | CreateUserRecruiterPartData>(data);
    } catch (error) {
      // Not conclusive: the profile may have been saved anyway (timeout, network cut after the
      // write) or exist already. The read below decides.
    }
    // The profile counts as saved when it can be read back (GET 200), whatever POST answered.
    // A read that failed on the browser side (network, unreadable answer; not 404 or 401) is
    // tried once more after a short pause: on 13/10 people pressed "Try again" dozens of times
    // while the server answered POST 201 and GET 200 every time.
    const readFailed = (r: Awaited<ReturnType<ReturnType<typeof fetchUserDetails>>>) =>
      fetchUserDetails.rejected.match(r) && r.payload !== 401;
    let fetched = await dispatch(fetchUserDetails(data.userId));
    if (readFailed(fetched)) {
      await new Promise((resolve) => setTimeout(resolve, PROFILE_REREAD_DELAY_MS));
      fetched = await dispatch(fetchUserDetails(data.userId));
    }
    if (fetchUserDetails.fulfilled.match(fetched) && fetched.payload !== PROFILE_NOT_FOUND) {
      return;
    }
    // POST answered 201: the backend holds the profile (just created, or already there: it
    // answers 201 "already exists" too). Reads still failing in the browser must not keep the
    // person on the form; read again in the background.
    if (posted && readFailed(fetched)) {
      setTimeout(() => { dispatch(fetchUserDetails(data.userId)); }, PROFILE_REREAD_DELAY_MS);
      return;
    }
    return rejectWithValue('');
  }
);

/** Delay before reading the profile again when the read right after POST failed. */
export const PROFILE_REREAD_DELAY_MS = 1500;

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    resetUserState: (state) => {
      localStorage.clear();
      Object.assign(state, initialState);
    },
    handleUpdateUserData: (state, action: PayloadAction<User<UserRole>>) => {
      state.data = action.payload;
    },
    resetUpdateUserStatus: (state) => {
      state.updateUser = 'init';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createNewUser.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(createNewUser.fulfilled, (state) => {
        state.status = 'succeeded';
        state.error = null;
        state.profileMissing = false;
      })
      .addCase(createNewUser.rejected, (state, action) => {
        state.status = 'failed';
        state.data = null;
        state.error = action.error.message || 'Failed to create new user';
      })
      .addCase(fetchUserDetails.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchUserDetails.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.error = null;
        if (action.payload === PROFILE_NOT_FOUND) {
          state.data = null;
          state.profileMissing = true;
        } else if (action.payload !== undefined) {
          state.data = action.payload as User<UserRole>;
          state.profileMissing = false;
        }
      })
      .addCase(fetchUserDetails.rejected, (state, action) => {
        state.status = 'failed';
        state.data = null;
        state.profileMissing = false;
        state.error = action.payload !== undefined
          ? `Failed to fetch user details (${action.payload})`
          : action.error.message || 'Failed to fetch user details';
      })
      .addCase(updateUserDetails.pending, (state) => {
        state.status = 'loading';
        state.updateUser = 'loading';
      })
      .addCase(updateUserDetails.fulfilled, (state) => {
        state.status = 'succeeded';
        state.error = null;
        state.updateUser = 'succeeded';
      })
      .addCase(updateUserDetails.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to update user details';
        state.updateUser = 'failed';
      });
  },
});

export const {
  resetUserState,
  handleUpdateUserData,
  resetUpdateUserStatus,
} = userSlice.actions;

export default userSlice.reducer;
